import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, Gift } from "lucide-react";

import { featuredWithStudio } from "@/lib/studio";
import { fetchPublishedStudioPieces } from "@/lib/studio-public.functions";
import { HIGHLIGHT_BADGES, PERFECT_FOR, REVIEWS, WHAT_ELLA_MAKES } from "@/lib/content";
import {
  absUrl,
  DESCRIPTION,
  EMAIL,
  GOOGLE_REVIEWS_URL,
  OG_IMAGE,
  OG_IMAGE_ALT,
  PHONE_E164,
  SITE_NAME,
  SITE_URL,
  TITLE,
} from "@/lib/site";
import { jsonLdScript, pageHead, PAGE_SEO } from "@/lib/seo";
import { avifSrcSet, photos } from "@/lib/photos";

import { Reveal } from "@/components/site/reveal";
import { Photo } from "@/components/site/photo";
import { Eyebrow } from "@/components/site/eyebrow";
import { CollectionCard } from "@/components/site/collection-card";
import { ReviewCard } from "@/components/site/review-card";

const HERO_SIZES = "(min-width: 1024px) 50vw, 100vw";
const FEATURED_SIZES = "(min-width: 1024px) 30vw, (min-width: 640px) 50vw, 100vw";

export const Route = createFileRoute("/")({
  loader: async () => {
    const studio = await fetchPublishedStudioPieces();
    return { featured: featuredWithStudio(studio) };
  },
  component: Home,
  head: () => ({
    ...pageHead({
      ...PAGE_SEO.home,
      extraLinks: [
        {
          rel: "alternate",
          type: "text/markdown",
          href: absUrl("/llms-full.txt"),
          title: "LLM-friendly brief",
        },
      ],
      preload: {
        href: photos.coral.avif,
        imageSrcSet: avifSrcSet(photos.coral),
        imageSizes: HERO_SIZES,
      },
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": `${SITE_URL}/#website`,
              url: absUrl("/"),
              name: SITE_NAME,
              description: DESCRIPTION,
              inLanguage: "en-US",
              publisher: { "@id": `${SITE_URL}/#business` },
            },
            {
              "@type": "WebPage",
              "@id": `${SITE_URL}/#webpage`,
              url: absUrl("/"),
              name: TITLE,
              description: DESCRIPTION,
              isPartOf: { "@id": `${SITE_URL}/#website` },
              about: { "@id": `${SITE_URL}/#business` },
              primaryImageOfPage: {
                "@type": "ImageObject",
                url: OG_IMAGE,
                caption: OG_IMAGE_ALT,
              },
            },
            {
              "@type": "LocalBusiness",
              "@id": `${SITE_URL}/#business`,
              name: SITE_NAME,
              description: DESCRIPTION,
              url: absUrl("/"),
              image: OG_IMAGE,
              telephone: PHONE_E164,
              email: EMAIL,
              priceRange: "$",
              areaServed: [
                { "@type": "City", name: "Midland", addressRegion: "MI" },
                { "@type": "AdministrativeArea", name: "Michigan" },
                { "@type": "Country", name: "US" },
              ],
              address: {
                "@type": "PostalAddress",
                addressLocality: "Midland",
                addressRegion: "MI",
                addressCountry: "US",
              },
              sameAs: [GOOGLE_REVIEWS_URL],
              founder: { "@id": `${SITE_URL}/#ella` },
              makesOffer: {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Product",
                  name: "Handmade 18-inch doll clothes",
                  description:
                    "Knit, crocheted, and sewn outfits for 18-inch dolls, including personalized made-to-order gifts.",
                },
              },
            },
            {
              "@type": "Person",
              "@id": `${SITE_URL}/#ella`,
              name: "Ella Wise",
              jobTitle: "Owner & Maker",
              worksFor: { "@id": `${SITE_URL}/#business` },
              homeLocation: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  addressLocality: "Midland",
                  addressRegion: "MI",
                  addressCountry: "US",
                },
              },
            },
          ],
        }),
      ],
    }),
  }),
});

function Home() {
  const { featured } = Route.useLoaderData();
  const highlightReview = REVIEWS[0];

  return (
    <main id="main" tabIndex={-1}>
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-56 -top-48 size-[34rem] rounded-full bg-rose-tint/40 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 top-40 size-[26rem] rounded-full bg-gold-soft/25 blur-3xl"
        />

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 pb-20 pt-12 sm:gap-16 sm:pb-24 sm:pt-16 lg:grid-cols-[1.05fr_1fr] lg:px-10 lg:pb-32 lg:pt-24">
          <div>
            <Eyebrow>HANDMADE IN MIDLAND, MICHIGAN</Eyebrow>
            <h1 className="mt-6 text-[2.35rem] leading-[1.08] sm:text-6xl lg:text-[4.25rem] lg:leading-[1.03]">
              Doll clothes made with{" "}
              <em className="accent-underline font-display italic text-primary">love.</em>
            </h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Beautiful, affordable handmade outfits for 18-inch American dolls — knit, crocheted,
              and sewn by hand, one stitch at a time. Perfect for gifts little ones will treasure.
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/collection"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-soft)] transition-transform hover:-translate-y-0.5"
              >
                Browse All Items <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
              <Link
                to="/personalization"
                className="inline-flex min-h-11 items-center justify-center rounded-full border border-primary/50 px-7 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
              >
                Ask About Personalization
              </Link>
            </div>

            <ul className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-7 sm:gap-y-3">
              {[
                "Handmade, never mass-produced",
                "Fits 18-inch American dolls",
                "Personalization available",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="inline-flex size-5 items-center justify-center rounded-full bg-accent text-accent-foreground">
                    <Check className="size-3" aria-hidden="true" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute -inset-6 -rotate-3 rounded-[46%_54%_38%_62%/52%_38%_62%_48%] bg-rose-tint/70"
            />
            <Photo
              {...photos.coral}
              alt={OG_IMAGE_ALT}
              eager
              sizes={HERO_SIZES}
              className="relative w-full rounded-[2rem] object-cover shadow-[var(--shadow-lift)]"
            />

            <div className="absolute -left-3 top-10 max-w-[13rem] rounded-2xl bg-card/95 p-4 shadow-[var(--shadow-soft)] backdrop-blur sm:-left-8">
              <p className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-gold-ink">
                All by hand
              </p>
              <p className="mt-1.5 font-display text-base leading-snug">Knit • Crochet • Sewn</p>
            </div>

            <div className="absolute -bottom-6 right-0 flex max-w-[14rem] items-center gap-3 rounded-2xl bg-plum p-4 text-plum-foreground shadow-[var(--shadow-lift)] sm:-right-6">
              <Gift className="size-5 shrink-0 text-gold" aria-hidden="true" />
              <p className="text-sm font-medium leading-snug">Made-to-order gifts</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-plum-deep py-7 text-plum-foreground">
        <ul className="mx-auto grid max-w-7xl grid-cols-2 gap-y-6 px-5 lg:grid-cols-4 lg:px-10">
          {HIGHLIGHT_BADGES.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-center justify-center gap-3">
              <Icon className="size-5 text-gold" aria-hidden="true" />
              <span className="text-sm font-medium tracking-wide">{label}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="relative overflow-hidden py-16 sm:py-24 lg:py-32">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-[-12rem] top-24 size-[30rem] rounded-full bg-gold-soft/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>THE COLLECTION</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              New styles,{" "}
              <em className="accent-underline font-display italic text-primary">classic</em> charm.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              A few favorites from Ella's handmade lineup — from everyday play outfits to
              special-occasion dresses.
            </p>
          </Reveal>

          <ul className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((item, i) => (
              <CollectionCard
                key={item.slug}
                item={item}
                description={item.copy}
                delay={i * 90}
                sizes={FEATURED_SIZES}
              />
            ))}
          </ul>

          <Reveal className="mt-16">
            <div className="flex flex-col items-center gap-6 rounded-3xl bg-accent px-8 py-10 text-center md:flex-row md:justify-between md:text-left">
              <p className="max-w-2xl font-display text-xl leading-snug text-accent-foreground sm:text-2xl">
                See something you love? Explore the full collection, then get in touch — Ella will
                help you find the perfect piece.
              </p>
              <Link
                to="/collection"
                className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                View the Full Collection <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>WHAT ELLA MAKES</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Dresses, gowns, sets, and{" "}
              <em className="accent-underline font-display italic text-primary">
                the little extras.
              </em>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Every piece is knit, crocheted, or sewn by hand and sized for 18-inch American dolls.
              Ella works in all three crafts, which is why one outfit can have crochet texture, a
              knit softness, and a properly sewn finish.
            </p>
          </Reveal>

          <ul className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {WHAT_ELLA_MAKES.map((item, i) => (
              <Reveal as="li" key={item.title} delay={i * 80}>
                <div className="h-full rounded-3xl border border-border/70 bg-card p-6 shadow-[var(--shadow-soft)]">
                  <h3 className="font-display text-lg">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </ul>

          <Reveal className="mt-10">
            <Link
              to="/collection"
              className="inline-flex min-h-11 items-center gap-2 rounded-full border border-primary/50 px-7 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-card"
            >
              See what's in the collection <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>PERFECT FOR</Eyebrow>
            <h2 className="mt-5 text-3xl leading-[1.1] sm:text-4xl">
              Gifts for the moments that{" "}
              <em className="accent-underline font-display italic text-primary">matter.</em>
            </h2>
          </Reveal>
          <ul className="mt-9 flex flex-wrap gap-3">
            {PERFECT_FOR.map((label) => (
              <li
                key={label}
                className="rounded-full border border-gold/50 bg-card px-5 py-2.5 text-sm font-medium text-foreground shadow-[var(--shadow-soft)]"
              >
                {label}
              </li>
            ))}
          </ul>
          <Reveal className="mt-8">
            <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
              Most of Ella's pieces are bought as gifts by parents and grandparents. Tell her the
              occasion and she'll help you choose — or make something{" "}
              <Link to="/personalization" className="text-gold-ink underline underline-offset-4">
                just for that day
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 sm:pb-24 lg:pb-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <div className="grid gap-10 rounded-[2rem] bg-accent px-8 py-12 sm:px-12 lg:grid-cols-[0.8fr_1fr]">
              <div>
                <Eyebrow>WHY HANDMADE MATTERS</Eyebrow>
                <h2 className="mt-5 text-3xl leading-[1.1] text-accent-foreground sm:text-4xl">
                  Made by a person,{" "}
                  <em className="font-display italic text-primary">not a machine.</em>
                </h2>
              </div>
              <div className="space-y-4 text-base leading-relaxed text-accent-foreground/80">
                <p>
                  A factory outfit is made by the thousand. Ella's are made one at a time, by one
                  person, in Midland, Michigan — which means small details can be changed, colors
                  can be chosen, and nothing leaves unless she's happy with it.
                </p>
                <p>
                  Handmade also lasts differently. Stitched edges, real trims, and honest
                  construction hold up to the way children actually play, and the pieces often stay
                  in a family long after the doll has been outgrown.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-plum py-16 text-plum-foreground sm:py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-[1fr_1.05fr] lg:px-10">
          <Reveal>
            <Eyebrow>MADE JUST FOR THEM</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Personalized pieces. <em className="font-display italic text-gold">Unforgettable</em>{" "}
              gifts.
            </h2>
            <p className="mt-6 max-w-xl leading-relaxed text-plum-foreground/75">
              Have a favorite color, a birthday theme, or an outfit you've pictured in your head?
              Ella takes made-to-order requests so the finished piece feels like it was made for one
              child only.
            </p>
            <div className="mt-8 stitch-rule text-gold" aria-hidden="true" />
            <Link
              to="/personalization"
              className="mt-8 inline-flex min-h-11 items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-plum-deep transition-transform hover:-translate-y-0.5"
            >
              Learn About Custom Orders <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Reveal>

          <Reveal delay={140}>
            <div className="grid grid-cols-2 gap-5 sm:gap-7">
              <figure>
                <Photo
                  {...photos.bridal}
                  alt="Handmade white crochet doll bridal gown with satin bow, tulle veil and pink flower crown"
                  sizes="(min-width: 1024px) 25vw, 45vw"
                  className="aspect-[4/5] w-full rounded-3xl object-cover shadow-[var(--shadow-lift)]"
                />
                <figcaption className="mt-3 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-gold">
                  The pieces
                </figcaption>
              </figure>
              <figure className="sm:mt-12">
                <Photo
                  {...photos.dollBridal}
                  alt="18-inch doll wearing a handmade ivory bridal gown, veil and flower crown"
                  sizes="(min-width: 1024px) 25vw, 45vw"
                  className="aspect-[4/5] w-full rounded-3xl object-cover shadow-[var(--shadow-lift)]"
                />
                <figcaption className="mt-3 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-gold">
                  Styled on the doll
                </figcaption>
              </figure>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>CUSTOMER LOVE</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Little <em className="accent-underline font-display italic text-primary">smiles</em>{" "}
              say it all.
            </h2>
          </Reveal>

          <ul className="mt-14 grid gap-7 md:max-w-2xl">
            <ReviewCard quote={highlightReview.quote} name={highlightReview.name} />
          </ul>

          <Reveal className="mt-10">
            <Link
              to="/about"
              hash="reviews"
              className="inline-flex min-h-11 items-center gap-2 rounded-full border border-primary/50 px-7 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
            >
              More reviews <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-plum-deep py-16 text-plum-foreground sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>GET IN TOUCH</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Ready to find something <em className="font-display italic text-gold">special?</em>
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-plum-foreground/75">
              Ask about a specific item, sizing for 18-inch dolls, or a custom order for a gift.
              Ella answers personally and is always happy to help you choose.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex min-h-11 items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-plum-deep transition-transform hover:-translate-y-0.5"
            >
              Contact Ella <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
